import React from 'react'
import { useState } from 'react'
import { pdf } from '@react-pdf/renderer'
import type { DocumentProps } from '@react-pdf/renderer'
import JSZip from 'jszip'
import type { WireArticleFull } from '../api/types'
import { registerFonts } from './theme'
import { ArticleReport } from './documents/ArticleReport'

function titleToSlug(title: string): string {
  return title
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .slice(0, 60)
    .replace(/-+$/, '')
}

/**
 * Fetches a remote image URL and converts it to a data URI for embedding in
 * the PDF. Detects the image format from magic bytes instead of relying on the
 * Content-Type header, which S3 may serve as application/octet-stream.
 * @react-pdf/renderer requires a proper `data:image/*` prefix to embed images.
 *
 * Returns '' on any failure so the PDF skips the image cleanly.
 */
/** Converts a raw ArrayBuffer to a base64 data URI, sniffing mime type from magic bytes. */
function arrayBufferToDataUri(buffer: ArrayBuffer): string {
  const bytes = new Uint8Array(buffer)

  // Detect format from magic bytes
  let mimeType = 'image/jpeg'
  if (bytes[0] === 0x89 && bytes[1] === 0x50 && bytes[2] === 0x4e && bytes[3] === 0x47) {
    mimeType = 'image/png'
  } else if (bytes[0] === 0x47 && bytes[1] === 0x49 && bytes[2] === 0x46) {
    mimeType = 'image/gif'
  } else if (
    bytes[0] === 0x52 && bytes[1] === 0x49 && bytes[2] === 0x46 && bytes[3] === 0x46 &&
    bytes[8] === 0x57 && bytes[9] === 0x45 && bytes[10] === 0x42 && bytes[11] === 0x50
  ) {
    mimeType = 'image/webp'
  }

  // Convert to base64 in chunks to avoid call-stack overflow on large images.
  const CHUNK = 8192
  const parts: string[] = []
  for (let i = 0; i < bytes.length; i += CHUNK) {
    parts.push(String.fromCharCode(...bytes.subarray(i, Math.min(i + CHUNK, bytes.length))))
  }
  return `data:${mimeType};base64,${btoa(parts.join(''))}`
}

async function toDataUri(url: string): Promise<string> {
  try {
    const res = await fetch(url)
    if (!res.ok) {
      console.warn(`[ArticlePdf] Image proxy failed ${res.status}:`, url)
      return ''
    }
    const buffer = await res.arrayBuffer()
    const dataUri = arrayBufferToDataUri(buffer)
    console.debug('[ArticlePdf] toDataUri bytes:', new Uint8Array(buffer).length)
    return dataUri
  } catch (err) {
    console.warn('[ArticlePdf] Image conversion failed:', url, err)
    return ''
  }
}

/**
 * ── .visual thumbnail extraction for PDF export ──────────────────────────────
 *
 * A .visual is a zip archive containing a metadata.json and content files.
 * There are four display/content type combinations, and the expected PDF
 * behavior differs for each:
 *
 *  1. STATIC POPUP (target: "popup", type: "static")
 *     - In the article body: a thumbnail image appears inline; clicking it
 *       opens the full PDF in a new window.
 *     - In the exported article PDF: the thumbnail image should appear in the
 *       same position in the text. The full print PDF (file.print.noSourced)
 *       would ideally be appended at the end of the article — NOT YET DONE.
 *     - Test artifact: needs a .visual zip with a thumbnail jpg/png and a
 *       metadata.json where target="popup" and type="static".
 *
 *  2. STATIC INLINE (target: "inline", type: "static")
 *     - In the article body: the inline image is displayed at full width.
 *     - In the exported article PDF: the inline image should appear at the
 *       same position at full width.
 *     - Test artifact: needs a .visual zip with an inlineImage jpg/png and a
 *       metadata.json where target="inline" and type="static".
 *
 *  3. INTERACTIVE INLINE (target: "inline", type: "interactive")
 *     - In the article body: an HTML SPA renders inside an iframe.
 *     - In the exported article PDF: a placeholder image (inlineImage, almost
 *       always placeholder.jpg) should appear inline; a PDF version
 *       (file.print.noSourced, typically NSmainPDF.pdf) would ideally be
 *       appended at the end — NOT YET DONE.
 *     - Test artifact: needs a .visual zip with a placeholder.jpg and a
 *       metadata.json where target="inline" and type="interactive".
 *
 *  4. INTERACTIVE POPUP (target: "popup", type: "interactive")
 *     - In the article body: a thumbnail/placeholder image appears inline;
 *       clicking it opens the SPA in a popup window.
 *     - In the exported article PDF: same as interactive inline — placeholder
 *       image inline, print PDF appended — NOT YET DONE.
 *     - Test artifact: needs a .visual zip with a placeholder.jpg and a
 *       metadata.json where target="popup" and type="interactive".
 *
 * CURRENT IMPLEMENTATION: For all four types, we fetch the zip, read
 * metadata.json to find the `inlineImage` filename, extract that file, and
 * render it as an image in the PDF. This covers the most visible gap (no
 * image at all). Appending print PDFs for popup and interactive visuals is a
 * separate future task.
 *
 * NOTE TO RUTH: To test this, you need an article that has visual blocks with
 * uploaded .visual artifacts. Please try one of each type if possible:
 *   - A static popup visual
 *   - A static inline visual
 *   - An interactive inline or popup visual
 * If you need help creating .visual test packages to upload, let @jeremy1015
 * know and he can build them for you.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 *
 * Fetches a .visual zip archive, reads metadata.json to find the inlineImage
 * filename, extracts that file, and returns it as a base64 data URI.
 * Returns '' on any failure so the PDF falls back to a text placeholder.
 */
async function extractVisualThumbnail(presignedUrl: string): Promise<string> {
  if (!presignedUrl) return ''
  try {
    const res = await fetch(presignedUrl)
    if (!res.ok) {
      console.warn('[ArticlePdf] Visual zip fetch failed:', res.status, presignedUrl)
      return ''
    }
    const buffer = await res.arrayBuffer()
    const zip = await JSZip.loadAsync(buffer)

    const metaFile = zip.file('metadata.json')
    if (!metaFile) { console.warn('[ArticlePdf] .visual missing metadata.json'); return '' }
    const raw = JSON.parse(await metaFile.async('string')) as { inlineImage?: string }
    const inlineImage = raw.inlineImage || 'placeholder.jpg'

    const imgFile = zip.file(inlineImage)
    if (!imgFile) { console.warn('[ArticlePdf] .visual missing inlineImage:', inlineImage); return '' }
    const imgBuffer = await imgFile.async('arraybuffer')
    return arrayBufferToDataUri(imgBuffer)
  } catch (err) {
    console.warn('[ArticlePdf] Visual thumbnail extraction failed:', presignedUrl, err)
    return ''
  }
}

/**
 * Walks the article and collects every image URL keyed by artifactId.
 * Returns a map of { artifactId → data URI } for all reachable image artifacts.
 */
async function buildImageMap(article: WireArticleFull): Promise<Record<string, string>> {
  const artifacts = article.artifacts ?? []

  // artifactId → presigned URL (deduplicated)
  const pending = new Map<string, string>()
  const visualPending = new Map<string, string>()

  function enqueue(artifactId: string) {
    if (pending.has(artifactId)) return
    const url = artifacts.find(a => a.id === artifactId)?.presignedUrl ?? ''
    if (url) pending.set(artifactId, url)
  }

  // Recursively walk blocks so photo/visual blocks inside textbox children are collected.
  function walkBlock(block: WireArticleFull['blocks'][number]) {
    if (block.type === 'photo') {
      enqueue(block.photo.artifactId)
    } else if (block.type === 'photo-group') {
      for (const photo of block.photos) enqueue(photo.artifactId)
    } else if (block.type === 'photo-comparison') {
      enqueue(block.before.artifactId)
      enqueue(block.after.artifactId)
    } else if (block.type === 'video') {
      if (block.posterArtifactId) enqueue(block.posterArtifactId)
    } else if (block.type === 'visual') {
      if (!visualPending.has(block.artifactId)) {
        const url = artifacts.find(a => a.id === block.artifactId)?.presignedUrl ?? ''
        if (url) visualPending.set(block.artifactId, url)
      }
    } else if (block.type === 'textbox') {
      for (const child of block.children) walkBlock(child)
    }
  }

  // Thumbnail — presignedUrl may be set directly on the thumbnail object rather than
  // (or in addition to) being in the artifacts array. Don't require artifactId to be present.
  if (article.thumbnail) {
    const id = article.thumbnail.artifactId
    const url = (id ? artifacts.find(a => a.id === id)?.presignedUrl : undefined)
      ?? article.thumbnail.presignedUrl
      ?? ''
    if (url) pending.set(id ?? '__thumbnail__', url)
  }

  // Walk all top-level blocks (recursion handles nested blocks in textboxes)
  for (const block of article.blocks) {
    walkBlock(block)
  }

  // Fetch all regular images in parallel
  const results = await Promise.allSettled(
    Array.from(pending.entries()).map(async ([id, url]) => ({
      id,
      dataUri: await toDataUri(url),
    })),
  )

  const map: Record<string, string> = {}
  for (const r of results) {
    if (r.status === 'fulfilled') map[r.value.id] = r.value.dataUri
  }

  // Visual blocks — fetch the .visual zip and extract the inline thumbnail.
  // Keyed by artifactId so duplicate uses of the same visual are fetched once.
  const visualResults = await Promise.allSettled(
    Array.from(visualPending.entries()).map(async ([id, url]) => ({
      id,
      dataUri: await extractVisualThumbnail(url),
    })),
  )
  for (const r of visualResults) {
    if (r.status === 'fulfilled' && r.value.dataUri) map[r.value.id] = r.value.dataUri
  }

  return map
}

export function useArticlePdf() {
  const [isGenerating, setIsGenerating] = useState(false)

  async function downloadPdf(article: WireArticleFull): Promise<void> {
    setIsGenerating(true)
    try {
      registerFonts()
      const imageMap = await buildImageMap(article)
      console.debug('[ArticlePdf] imageMap keys:', Object.keys(imageMap), 'thumbnail key:', article.thumbnail?.artifactId ?? '__thumbnail__', 'thumbnail src length:', (imageMap[article.thumbnail?.artifactId ?? '__thumbnail__'] ?? '').length)
      const element = React.createElement(ArticleReport, {
        article,
        imageMap,
        generatedAt: new Date(),
      })
      const blob = await pdf(element as React.ReactElement<DocumentProps>).toBlob()
      const url = URL.createObjectURL(blob)
      const anchor = document.createElement('a')
      anchor.href = url
      anchor.download = `${titleToSlug(article.title) || 'article'}.pdf`
      anchor.click()
      URL.revokeObjectURL(url)
    } finally {
      setIsGenerating(false)
    }
  }

  return { downloadPdf, isGenerating }
}
